import React, { useMemo } from "react";
import {
    allDayBlockClasses,
    displayDurationMinutes,
    displayId,
    displayLabel,
    displayTestId,
    displayTimeStr,
    timedAccentClasses,
    timedBlockClasses,
} from "./calendar-item-display";
import { CalendarItem } from "./calendar-types";
import {
    DAYS_OF_WEEK,
    HOURS,
    SLOTS_PER_HOUR,
    SUBROWS_PER_HOUR,
    SUBROWS_PER_SLOT,
    computeOverlapLayout,
    formatDateKey,
    formatTime,
    isSameDay,
    parseTimeToRow,
    tourDurationToSpan,
} from "./calendar-utils";

interface WeekGridProps {
    weekDays: Date[];
    today: Date;
    eventsByDate: Map<string, CalendarItem[]>;
    onTourClick: (event: CalendarItem) => void;
    onDayClick: (date: Date) => void;
}

export const WeekGrid: React.FC<WeekGridProps> = ({
    weekDays,
    today,
    eventsByDate,
    onTourClick,
    onDayClick,
}) => {
    const { allDayByDate, timedByDate } = useMemo(() => {
        const allDay = new Map<string, CalendarItem[]>();
        const timed = new Map<string, CalendarItem[]>();
        Array.from(eventsByDate.entries()).forEach(([dateKey, events]) => {
            events.forEach(evt => {
                const target = displayTimeStr(evt) !== null ? timed : allDay;
                if (!target.has(dateKey)) {
                    target.set(dateKey, []);
                }
                target.get(dateKey)!.push(evt);
            });
        });
        return { allDayByDate: allDay, timedByDate: timed };
    }, [eventsByDate]);

    const hasAllDayEvents = allDayByDate.size > 0;

    return (
        <div className="isolate flex flex-auto flex-col overflow-auto bg-white border border-gray-200 rounded-lg">
            <div className="flex min-w-[800px] flex-none flex-col">
                <div className="sticky top-0 z-30 flex-none bg-white shadow ring-1 ring-black/5 pr-8">
                    <div className="-mr-px grid grid-cols-7 divide-x divide-gray-100 border-r border-gray-100 text-sm/6 text-gray-500">
                        <div className="col-end-1 w-14 sticky left-0 bg-white z-10" />
                        {weekDays.map((day, i) => {
                            const isToday = isSameDay(day, today);
                            return (
                                <button
                                    key={i}
                                    onClick={() => onDayClick(day)}
                                    className="flex items-center justify-center py-3 cursor-pointer hover:bg-gray-50">
                                    <span className="flex items-baseline">
                                        {DAYS_OF_WEEK[day.getDay()]}{" "}
                                        <span
                                            className={`ml-1.5 flex size-8 items-center justify-center font-semibold ${
                                                isToday
                                                    ? "rounded-full bg-brand-600 text-white"
                                                    : "text-gray-900"
                                            }`}>
                                            {day.getDate()}
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    {hasAllDayEvents ? (
                        <div className="-mr-px grid grid-cols-7 border-t border-gray-200">
                            <div className="col-end-1 w-14 sticky left-0 bg-white z-10" />
                            {weekDays.map((day, dayIndex) => {
                                const dateKey = formatDateKey(day);
                                const events = allDayByDate.get(dateKey) || [];
                                return (
                                    <div
                                        key={dayIndex}
                                        className={`py-1 px-0.5 space-y-0.5 min-h-8 ${dayIndex > 0 ? "border-l border-gray-100" : ""}`}>
                                        {events.map(evt => (
                                            <button
                                                key={displayId(evt)}
                                                onClick={() => onTourClick(evt)}
                                                data-testid={displayTestId(evt)}
                                                className={`block w-full text-left px-1.5 py-0.5 text-xs rounded truncate ${allDayBlockClasses(evt)}`}>
                                                {displayLabel(evt)}
                                            </button>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
                <div className="flex flex-auto">
                    <div className="sticky left-0 z-20 w-14 flex-none bg-white ring-1 ring-gray-100" />
                    <div className="grid flex-auto grid-cols-1 grid-rows-1">
                        <div
                            style={{
                                gridTemplateRows: `repeat(${HOURS.length * SLOTS_PER_HOUR}, minmax(1.5rem, 1fr))`,
                            }}
                            className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100">
                            <div className="row-end-1 h-7" />
                            {HOURS.map((hour, i) => (
                                <React.Fragment key={i}>
                                    <div>
                                        <div className="sticky left-0 z-30 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs/5 text-gray-400 bg-white">
                                            {hour}
                                        </div>
                                    </div>
                                    <div />
                                </React.Fragment>
                            ))}
                        </div>

                        <div className="col-start-1 col-end-2 row-start-1 grid grid-rows-1 divide-x divide-gray-100 grid-cols-7">
                            {weekDays.map((_, i) => (
                                <div key={i} className="row-span-full" />
                            ))}
                            <div className="col-start-8 row-span-full w-8" />
                        </div>

                        <ol
                            style={{
                                gridTemplateRows: `1.75rem repeat(${HOURS.length * SLOTS_PER_HOUR * SUBROWS_PER_SLOT}, minmax(0, 1fr)) auto`,
                            }}
                            className="col-start-1 col-end-2 row-start-1 z-10 grid grid-cols-7 pr-8">
                            {weekDays.map((day, dayIndex) => {
                                const dateKey = formatDateKey(day);
                                const eventsForDay = timedByDate.get(dateKey) || [];
                                const withRows = eventsForDay
                                    .map(evt => {
                                        const row = parseTimeToRow(displayTimeStr(evt));
                                        if (row === null) return null;
                                        const minutes = displayDurationMinutes(evt);
                                        return {
                                            event: evt,
                                            row,
                                            span: tourDurationToSpan(minutes, row),
                                        };
                                    })
                                    .filter(
                                        (
                                            item,
                                        ): item is {
                                            event: CalendarItem;
                                            row: number;
                                            span: number;
                                        } => item !== null,
                                    );
                                const laid = computeOverlapLayout(withRows);
                                return laid.map(({ event: evt, layout }) => {
                                    const widthPct = 100 / layout.totalColumns;
                                    const leftPct = layout.columnIndex * widthPct;
                                    const timeDisplay = formatTime(displayTimeStr(evt));
                                    return (
                                        <li
                                            key={displayId(evt)}
                                            style={{
                                                gridRow: `${layout.row} / span ${layout.span}`,
                                                gridColumnStart: dayIndex + 1,
                                            }}
                                            className="relative mt-px pointer-events-none">
                                            <button
                                                onClick={() => onTourClick(evt)}
                                                data-testid={displayTestId(evt)}
                                                style={{
                                                    left: `${leftPct}%`,
                                                    width: `${widthPct}%`,
                                                }}
                                                className={`pointer-events-auto group absolute inset-y-px flex flex-col overflow-hidden rounded-md text-xs text-left ${
                                                    layout.span < SUBROWS_PER_HOUR
                                                        ? "px-1.5 py-0"
                                                        : "p-1.5"
                                                } ${timedBlockClasses(evt)}`}>
                                                {layout.span < SUBROWS_PER_HOUR ? (
                                                    <p className="font-semibold text-white truncate leading-tight">
                                                        {displayLabel(evt)}
                                                        <span
                                                            className={`ml-1 font-normal ${timedAccentClasses(evt)}`}>
                                                            {timeDisplay}
                                                        </span>
                                                    </p>
                                                ) : (
                                                    <>
                                                        <p className="font-semibold text-white truncate">
                                                            {displayLabel(evt)}
                                                        </p>
                                                        <p
                                                            className={timedAccentClasses(
                                                                evt,
                                                            )}>
                                                            {timeDisplay}
                                                        </p>
                                                    </>
                                                )}
                                            </button>
                                        </li>
                                    );
                                });
                            })}
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    );
};
